import React, { useEffect, useState } from "react"

const unzoom = (box, scale) =>
  scale === 1
    ? box
    : {
        left: box.left / scale,
        right: box.right / scale,
        top: box.top / scale,
        bottom: box.bottom / scale,
        width: box.width / scale,
        height: box.height / scale,
      }
const rect = (node, scale) =>
  node ? unzoom(node.getBoundingClientRect(), scale) : undefined

// Connections describe existing stage/position provenance; they never place or
// advance a participant. Measure rendered cards so names and doubles can wrap.
// The canvas may be zoomed (CSS `zoom`): client rects come back in zoomed pixels
// while the SVG draws in the canvas's own units, so measurements are divided back.
export default function DrawConnections({ canvas, sections, zoom = 1 }) {
  const [drawing, setDrawing] = useState({ paths: [], width: 0, height: 0 })
  useEffect(() => {
    const root = canvas.current
    if (!root) return undefined
    const scale = root.currentCSSZoom ?? zoom ?? 1
    const measure = () => {
      const origin = unzoom(root.getBoundingClientRect(), scale)
      const nodes = Array.from(root.querySelectorAll("[data-bracket-position]"))
      const paths = []
      sections.forEach((section, s) => {
        const next = sections[s + 1]
        if (!next) return
        section.rows.forEach((row, i) => {
          const destination = section.qualification
            ? Math.ceil(Number(row.competition?.nextStagePosition) / 2)
            : Math.ceil(Number(row.competition?.position) / 2)
          const targetIndex = next.rows.findIndex(
            target => Number(target.competition?.position) === destination
          )
          const from = rect(
            nodes.find(node => node.dataset.bracketPosition === `${s}:${i}`),
            scale
          )
          const to = rect(
            nodes.find(
              node => node.dataset.bracketPosition === `${s + 1}:${targetIndex}`
            ),
            scale
          )
          if (!from?.width || !to?.width) return
          const x = from.right - origin.left + root.scrollLeft
          const y = from.top + from.height / 2 - origin.top + root.scrollTop
          const endX = to.left - origin.left + root.scrollLeft
          const endY = to.top + to.height / 2 - origin.top + root.scrollTop
          const mid = x + (endX - x) / 2
          paths.push(`M ${x} ${y} H ${mid} V ${endY} H ${endX}`)
        })
      })
      const width = Math.max(
        0,
        ...nodes.map(
          node => rect(node, scale).right - origin.left + root.scrollLeft
        )
      )
      const height = Math.max(
        0,
        ...nodes.map(
          node => rect(node, scale).bottom - origin.top + root.scrollTop
        )
      )
      setDrawing({ paths, width, height })
    }
    measure()
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure)
    observer?.observe(root)
    root
      .querySelectorAll("[data-bracket-position]")
      .forEach(node => observer?.observe(node))
    window.addEventListener("resize", measure)
    return () => {
      observer?.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [canvas, sections, zoom])
  return (
    <svg
      className="main-draw-connections"
      width={drawing.width}
      height={drawing.height}
      aria-hidden="true"
    >
      {drawing.paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  )
}
